import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { Creator, AuthResponse, ReportData, CatalogResponse } from "../../domain";

interface Cached<T> { data: T; etag: string }

export const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export class ApiClient {
  private client: AxiosInstance;
  private catalogCache = new Map<number, Cached<CatalogResponse>>();
  private reportCache = new Map<string, Cached<ReportData>>();

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
    });

    // Interceptor: автоматически добавляем Authorization header из localStorage
    this.client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem("access_token");
      if (token) {
        config.headers = config.headers || {} as any;
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    });

    // Interceptor: при 401 — очищаем токен и уходим на корень (лендинг)
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("auth_method");
          window.location.href = "/";
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth methods - Meta OAuth (Personal Accounts)
  async startMetaOAuth(state: string): Promise<string> {
    console.log("🔵 [Meta OAuth] Starting OAuth flow");
    const response = await this.client.get("/api/auth/meta/start", {
      params: { state },
    });
    return response.data.auth_url;
  }

  async metaOAuthCallback(code: string, state: string): Promise<AuthResponse> {
    console.log("🔵 [Meta OAuth] Processing callback");
    const response = await this.client.post("/api/auth/meta/callback", { code, state });
    console.log("🔵 [Meta OAuth] Callback successful");
    return response.data;
  }

  // Auth methods - Instagram Direct API (Creator Accounts)
  async startInstagramDirectOAuth(state: string): Promise<string> {
    console.log("🟣 [Instagram Direct] Starting OAuth flow");
    const response = await this.client.get("/api/auth/instagram/start", {
      params: { state },
    });
    return response.data.auth_url;
  }

  async instagramDirectOAuthCallback(code: string, state: string): Promise<AuthResponse> {
    console.log("🟣 [Instagram Direct] Processing callback");
    const response = await this.client.post("/api/auth/instagram/callback", { code, state });
    console.log("🟣 [Instagram Direct] Callback successful");
    return response.data;
  }

  // Legacy methods for backward compatibility
  async startOAuth(state: string): Promise<string> {
    return this.startMetaOAuth(state);
  }

  async oauthCallback(code: string, state: string): Promise<AuthResponse> {
    return this.metaOAuthCallback(code, state);
  }

  async getCreator(): Promise<Creator> {
    const response = await this.client.get("/api/auth/creator/me");
    return response.data;
  }

  async logout(): Promise<void> {
    localStorage.removeItem("access_token");
    localStorage.removeItem("auth_method");
  }

  // Demo / test mode
  async demoLogin(): Promise<string> {
    const response = await this.client.post("/api/auth/demo");
    return response.data.access_token;
  }

  // Report methods
  async generateReport(limit: number = 100): Promise<ReportData> {
    const response = await this.client.post("/api/reports", { limit });
    return response.data;
  }

  async refreshReport(limit: number = 100): Promise<ReportData> {
    const response = await this.client.post("/api/reports/refresh", { limit });
    return response.data;
  }

  async updateCreatorSettings(settings: { show_in_catalog?: boolean; catalog_message?: string }): Promise<Creator> {
    const response = await this.client.patch("/api/auth/creator/me", settings);
    return response.data;
  }

  async getCatalog(page: number = 1): Promise<CatalogResponse> {
    const cached = this.catalogCache.get(page) ?? this._loadCatalogPage(page);
    const response = await this.client.get("/api/catalog", {
      params: { page },
      headers: cached ? { "If-None-Match": cached.etag } : {},
      validateStatus: (s) => s === 200 || s === 304,
    });
    if (response.status === 304 && cached) return cached.data;
    const etag = response.headers["etag"];
    if (etag) this._saveCatalogPage(page, { data: response.data, etag });
    return response.data;
  }

  getCatalogCached(page: number): CatalogResponse | null {
    return (this.catalogCache.get(page) ?? this._loadCatalogPage(page))?.data ?? null;
  }

  private _saveCatalogPage(page: number, entry: Cached<CatalogResponse>): void {
    this.catalogCache.set(page, entry);
    try { sessionStorage.setItem(`catalog-p${page}`, JSON.stringify(entry)); } catch {}
  }

  private _loadCatalogPage(page: number): Cached<CatalogResponse> | null {
    try {
      const raw = sessionStorage.getItem(`catalog-p${page}`);
      if (raw) {
        const entry: Cached<CatalogResponse> = JSON.parse(raw);
        this.catalogCache.set(page, entry);
        return entry;
      }
    } catch {}
    return null;
  }

  async getPublicReport(username: string): Promise<ReportData> {
    const cached = this.reportCache.get(username);
    const response = await this.client.get(`/api/reports/public/${username}`, {
      headers: cached ? { "If-None-Match": cached.etag } : {},
      validateStatus: (s) => s === 200 || s === 304,
    });
    if (response.status === 304 && cached) return cached.data;
    const etag = response.headers["etag"];
    if (etag) this.reportCache.set(username, { data: response.data, etag });
    return response.data;
  }
}

export const apiClient = new ApiClient();
